import React from 'react';
import { WarningTwoTone } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Link } from 'react-router';
import {
  Button,
  Empty,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnType } from 'antd/es/table';
import { PRETTY_DATE_FORMAT } from '@owl-frontend/components';
import type { Claim } from '../../../api/claims/interface';
import { Locale, SessionContext } from '../../../context/AppProvider';
import { FeatureKeys } from '../../../data/session/interface';
import { ShellRouteDefs } from '../../ClaimOverviewShellContainer/ClaimOverviewLandingContainer';
import { generatePath, RouteDefs } from '../../Routing/Routing';
import styles from '../ClaimsContainer.module.scss';
import type { ClaimsHookState } from '../useClaimsHook';

dayjs.extend(utc);

const statusColors: Record<string, string> = {
  open: 'green',
  pending: 'yellow',
  closed: 'red',
  no_data: 'grey',
};

export const ClaimStatusComponent: React.FC<{
  claimStatus: 'Open' | 'Pending' | 'Closed' | string;
}> = ({ claimStatus }) => {
  const { messages } = React.useContext(Locale);

  if (!claimStatus) {
    return null;
  }

  return (
    <Tag
      color={
        claimStatus
          ? statusColors[claimStatus.toLowerCase()] || 'default'
          : 'default'
      }
    >
      {messages[`claims.hub.status.${claimStatus.toLowerCase}`] ?? claimStatus}
    </Tag>
  );
};

const EMPTY = '-';
interface Props {
  data: Claim[];
  loading: boolean;
  onSortChange(
    sort: { key: string[]; order: 'ascend' | 'descend' } | null
  ): void;
  onPageChange(data: {
    currentPage: number;
    pageSize: number;
    sort: ClaimsHookState['sort'];
  }): void;
  pagination: {
    currentPage: number;
    pageSize: number;
    total: number;
  };
  onClearFilter(): void;
}

const ClaimsListCustomerTermComponent: React.FC<Props> = ({
  data,
  loading,
  onSortChange,
  onPageChange,
  pagination,
  onClearFilter,
}) => {
  const { messages } = React.useContext(Locale);
  const { features } = React.useContext(SessionContext);

  const columns: ColumnType<Claim>[] = React.useMemo(
    () => [
      {
        key: 'client_dossier_id',
        dataIndex: 'client_dossier_id',
        title: messages['claims.hub.columns.client_dossier_id'],
        fixed: 'left' as const,
        width: 130,
        render: (client_dossier_id, dossier) => {
          if (dossier.graph_version) {
            return (
              <Link
                data-testid={`claim-link-${dossier.dossier_id}`}
                data-dd-privacy="allow"
                to={{
                  pathname: generatePath(
                    features[FeatureKeys.DemoClaimOverview]
                      ? ShellRouteDefs.ClaimOverviewShell
                      : RouteDefs.ClaimOverview,
                    {
                      dossierId: dossier.dossier_id,
                    }
                  ),
                  search: `?from=${RouteDefs.Claims}`,
                  hash: 'summaries',
                }}
                state={{ from: RouteDefs.Claims }}
              >
                {client_dossier_id}
              </Link>
            );
          }

          const tooltipTitle =
            dossier.status === 'no_data'
              ? messages['claims.hub.no_data']
              : dossier.status === 'error'
              ? messages['claims.hub.processing_error']
              : messages['claims.hub.processing'];

          const warningColor = dossier.status === 'no_data' ? 'grey' : 'orange';

          return (
            <Space>
              <div data-dd-privacy="allow">{client_dossier_id}</div>
              <Tooltip title={tooltipTitle} placement="top">
                <WarningTwoTone twoToneColor={[warningColor, '#fff']} />
              </Tooltip>
            </Space>
          );
        },
      },
      {
        key: 'status',
        dataIndex: ['data', 'claim_status'],
        title: messages['claims.hub.columns.status'],
        fixed: 'left' as const,
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: 170,
        render: (value: string) => <ClaimStatusComponent claimStatus={value} />,
      },
      {
        key: 'dossier_name',
        dataIndex: 'dossier_name',
        title: messages['claims.hub.columns.dossier_name'],
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: 170,
      },
      {
        key: 'age',
        dataIndex: ['data', 'birth_date'],
        title: messages['claims.hub.columns.birth_date'],
        sorter: false,
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: 100,
        render: (value: string) => {
          if (!value) {
            return EMPTY;
          }
          return dayjs().diff(dayjs(value), 'years');
        },
      },
      {
        key: 'disabilityDate',
        dataIndex: ['data', 'disability_begin_date'],
        title: messages['claims.hub.columns.date_of_disability'],
        sorter: true,
        sortDirections: ['descend', 'ascend', 'descend'],
        width: 170,
        render: (value: string) => {
          if (!value) {
            return EMPTY;
          }
          return dayjs(value).format(PRETTY_DATE_FORMAT);
        },
      },
      {
        key: 'primaryDiagnosis',
        dataIndex: ['data', 'diagnosis_code'],
        title: messages['claims.hub.columns.primary_diagnosis'],
        sorter: false,
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: 180,
        render: (icd_code, record) => {
          const description = record?.data?.primary_diagnosis_description ?? '';
          const displayText = description
            ? `${icd_code} - ${description}`
            : icd_code;

          return (
            <Tooltip title={description}>
              <div
                style={{
                  width: '145px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayText}
              </div>
            </Tooltip>
          );
        },
      },
      {
        key: 'occupation',
        dataIndex: ['data', 'job_title_code'],
        title: messages['claims.hub.columns.occupation'],
        sorter: false,
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: 150,
      },
      {
        key: 'occupation_type',
        dataIndex: ['data', 'occupation_code'],
        title: messages['claims.hub.columns.occupation_type'],
        sorter: false,
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: 170,
      },
      {
        key: 'assignee_full_name',
        dataIndex: 'assignee_full_name',
        title: messages['claims.hub.columns.assignee'],
        width: 200,
        render: (value: string) => {
          return <div data-dd-privacy="allow">{value}</div>;
        },
      },
      {
        key: 'cidDate',
        dataIndex: ['data', 'cid_date'],
        title: messages['claims.hub.columns.cid_date'],
        fixed: 'right' as const,
        width: 150,
        render: (value: string) => {
          if (!value) {
            return EMPTY;
          }

          return dayjs(value).utc().format(PRETTY_DATE_FORMAT);
        },
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        defaultSortOrder: 'descend' as const,
      },
      {
        key: 'updated_at',
        dataIndex: 'updated_at',
        title: messages['claims.hub.columns.updated_at'],
        fixed: 'right' as const,
        sorter: true,
        sortDirections: ['descend', 'ascend', 'descend'],
        width: 150,
        render: (last_updated) => {
          return dayjs(last_updated).format(PRETTY_DATE_FORMAT);
        },
      },
    ],
    [features, messages]
  );

  const handleTableChange = React.useCallback(
    (newPagination, _, sorter) => {
      onPageChange({
        currentPage: newPagination.current,
        pageSize: newPagination.pageSize,
        sort: {
          key: sorter.column.dataIndex,
          order: sorter.order,
        },
      });
      if (!sorter.column) {
        onSortChange(null);
        return;
      }
    },
    [onPageChange, onSortChange]
  );

  return (
    <Spin spinning={loading}>
      <Table<Claim>
        rowKey="dossier_id"
        dataSource={data}
        columns={columns}
        onChange={handleTableChange}
        locale={{
          emptyText: (
            <Empty
              description={
                <Space
                  className={styles.emptyContainerComponents}
                  direction="vertical"
                >
                  <Typography.Text>
                    {messages['claims.hub.empty']}
                  </Typography.Text>
                  <Button type="primary" onClick={onClearFilter}>
                    {messages['claims.hub.filters.clear']}
                  </Button>
                </Space>
              }
            />
          ),
        }}
        scroll={{
          x: 'max-content',
          y: features[FeatureKeys.SelfServiceView]
            ? 'calc(100vh - 425px)'
            : 'calc(100vh - 385px)',
        }}
        pagination={{
          current: pagination.currentPage,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
      />
    </Spin>
  );
};

export default ClaimsListCustomerTermComponent;
